import { cloneDeep } from 'lodash';
import { pkiCertStatusTypes } from 'client/Pki/Constants';
import PkiApi from 'client/api/v2/PkiApi';
import Vue from 'vue';

const { revoked } = pkiCertStatusTypes;

const DispatchStatus = {
  ERROR: 'error',
  IDLE: 'idle',
  PENDING: 'pending',
  SUCCESS: 'success',
};

const DispatchType = {
  ENABLE: 'enable',
  REVOKE: 'revoke',
  FETCH: 'fetch',
};

const statusCodeToDispatchStatus = (code = null) => {
  let status = DispatchStatus.IDLE;

  switch (code) {
    case 200:
    case 201:
      status = DispatchStatus.SUCCESS;
      break;
    case 400:
    case 401:
    case 402:
    case 403:
    case 404:
    case 500:
    default:
      status = DispatchStatus.ERROR;
      break;
  }

  return status;
};

const apiAction = async (commit, dispatchType, method) => {
  const statusMutation = `SET_${dispatchType.toUpperCase()}_STATUS`;

  commit(statusMutation, DispatchStatus.PENDING);

  const result = await method();
  const dispatchStatus = statusCodeToDispatchStatus(result.status);

  result.dispatchStatus = dispatchStatus;

  commit(statusMutation, dispatchStatus);

  return result;
};

const defaultState = {
  ids: [],
  items: new Map(),
  itemCount: 0,
  enableStatus: DispatchStatus.IDLE,
  fetchStatus: DispatchStatus.IDLE,
  revokeStatus: DispatchStatus.IDLE,
};

// @todo: error handling + notifications

export default {
  namespaced: true,

  state: cloneDeep(defaultState),

  getters: {
    getOne({ state }) {
      return id => state.items.get(id);
    },

    getAll({ state }) {
      return (ids = []) => (ids.length ? ids : state.ids).map(id => state.items.get(id));
    },
  },

  actions: {
    async enable({ commit }, { groupType, groupIds, certificateLength }) {
      const { data: enabledCerts, dispatchStatus, totalCount } = await apiAction(
        commit,
        DispatchType.ENABLE,
        () => PkiApi.enable(groupType, groupIds, certificateLength),
      );

      if (dispatchStatus === DispatchStatus.SUCCESS) {
        commit('ADD_ALL', enabledCerts);
        commit('SET_TOTAL_COUNT', totalCount);
      }
    },

    async fetchOne({ commit }, id) {
      const { data: item, dispatchStatus, totalCount } = await apiAction(
        commit,
        DispatchType.FETCH,
        () => PkiApi.fetchById(id),
      );

      if (dispatchStatus === DispatchStatus.SUCCESS) {
        commit('ADD_ONE', item);
        commit('SET_TOTAL_COUNT', totalCount);
      }
    },

    async fetchAll({ commit }, params) {
      const { data: items, dispatchStatus, totalCount } = await apiAction(
        commit,
        DispatchType.FETCH,
        () => PkiApi.fetchByParams(params),
      );

      if (dispatchStatus === DispatchStatus.SUCCESS) {
        commit('ADD_ALL', items);
        commit('SET_TOTAL_COUNT', totalCount);
      }
    },

    async revokeById({ commit, getters }, id) {
      const { dispatchStatus } = await apiAction(
        commit,
        DispatchType.REVOKE,
        () => PkiApi.revokeById(id),
      );

      if (dispatchStatus === DispatchStatus.SUCCESS) {
        const item = cloneDeep(getters.getOne(id));
        item.status = revoked;

        commit('SET_ONE', item);
      }
    },

    async revokeAll({ commit, getters }, ids) {
      const { dispatchStatus } = await apiAction(
        commit,
        DispatchType.REVOKE,
        () => PkiApi.revokeAll(ids),
      );

      if (dispatchStatus === DispatchStatus.SUCCESS) {
        ids.forEach(id => {
          const item = cloneDeep(getters.getOne(id));
          item.status = revoked;

          commit('SET_ONE', item);
        });
      }
    },
  },

  mutations: {
    ADD_ONE(state, item) {
      const { _id } = item;

      state.items.set(_id, item);
      Vue.set(state, 'ids', state.items.keys());
    },

    ADD_ALL(state, items) {
      items.forEach(item => state.items.set(item._id, item));
      Vue.set(state, 'ids', state.items.keys());
    },

    SET_ENABLE_STATUS(state, status = DispatchStatus.IDLE) {
      Vue.set(state, 'enableStatus', status);
    },

    SET_REVOKE_STATUS(state, status = DispatchStatus.IDLE) {
      Vue.set(state, 'deleteStatus', status);
    },

    SET_FETCH_STATUS(state, status = DispatchStatus.IDLE) {
      Vue.set(state, 'fetchStatus', status);
    },

    SET_ONE(state, item) {
      state.items.set(item._id, item);
    },

    SET_TOTAL_COUNT(state, count) {
      Vue.set(state, 'totalCount', count);
    },
  },
};
